
import React, { useState } from 'react';
import { AppConfig, AIEvent, ReviewItem, Theme } from '../types';
import { geminiService } from '../services/geminiService';
import { storageService } from '../services/storageService';
import { supabaseService } from '../services/supabaseService';
import { googleSheetsService } from '../services/googleSheetsService';

interface DiscoveryToolProps {
  config: AppConfig;
  existingEvents: AIEvent[];
  onDiscoverComplete: () => void;
  theme: Theme;
}

const DiscoveryTool: React.FC<DiscoveryToolProps> = ({ config, existingEvents, onDiscoverComplete, theme }) => {
  const [isSearching, setIsSearching] = useState(false);
  const [progress, setProgress] = useState('');
  const [summary, setSummary] = useState<{ found: number; new: number; skipped: number } | null>(null);
  const [allKeywords, setAllKeywords] = useState<string[]>(config.discoveryKeywords);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [directSync, setDirectSync] = useState(false);

  const toggleKeyword = (keyword: string) => {
    if (selectedKeywords.includes(keyword)) {
      setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
    } else {
      setSelectedKeywords([...selectedKeywords, keyword]);
    }
  };

  const addKeyword = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !allKeywords.includes(trimmed)) {
      setAllKeywords([...allKeywords, trimmed]);
      setNewKeyword('');
    }
  };

  const deleteKeyword = (keyword: string) => {
    setAllKeywords(allKeywords.filter(k => k !== keyword));
    setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
  };

  const selectAll = () => setSelectedKeywords(allKeywords);
  const clearAll = () => setSelectedKeywords([]);
  const refreshKeywords = () => {
    setAllKeywords(config.discoveryKeywords);
    setSelectedKeywords([]);
  };

  const generateEventId = (name: string, year: number) => {
    const cleanName = name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5).toUpperCase();
    const randomSuffix = Math.random().toString(36).substr(2, 3).toUpperCase();
    return `${cleanName}-${year}-${randomSuffix}`;
  };

  // Helper: Normalize URL (remove protocol, www, trailing slash)
  const normalizeUrl = (url: string) => {
    if (!url) return '';
    return url.toLowerCase()
      .replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
      .replace(/\/$/, '')
      .trim();
  };

  // Helper: Normalize Name (remove non-alphanumeric, lowercase)
  const normalizeName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const isDuplicateEvent = (candidate: AIEvent, database: AIEvent[]): boolean => {
    const candidateUrl = normalizeUrl(candidate.url);
    const candidateName = normalizeName(candidate.campaignName);
    
    return database.some(existing => {
      // 1. Strong Match: URL
      if (candidateUrl && existing.url) {
        const existingUrl = normalizeUrl(existing.url);
        if (existingUrl === candidateUrl) {
          console.debug(`[Dedupe] URL Match: ${candidate.url} == ${existing.url}`);
          return true;
        }
      }

      // 2. Strong Match: Name + Start Date
      const datesMatch = existing.startDate === candidate.startDate;
      
      if (datesMatch) {
        const existingName = normalizeName(existing.campaignName);
        
        // Exact Name Match
        if (existingName === candidateName) {
           console.debug(`[Dedupe] Exact Name+Date: ${candidate.campaignName}`);
           return true;
        }

        // Fuzzy Containment Match (e.g. "AI Summit" vs "The AI Summit 2025")
        // Only run this if names are sufficiently long to avoid false positives on short acronyms
        if (candidateName.length > 3 && existingName.length > 3) {
          if (existingName.includes(candidateName) || candidateName.includes(existingName)) {
            console.debug(`[Dedupe] Fuzzy Name+Date: "${candidate.campaignName}" overlaps with "${existing.campaignName}"`);
            return true;
          }
        }
      }

      return false;
    });
  };

  const startDiscovery = async () => {
    if (selectedKeywords.length === 0) return;
    
    setIsSearching(true);
    setSummary(null);
    setProgress(`Targeting ${selectedKeywords.length} focus areas...`);
    
    try {
      const discovered = await geminiService.discoverEvents(selectedKeywords);
      setProgress(`Comparing ${discovered.length} candidates against ${existingEvents.length} existing records...`);
      
      const newItems: AIEvent[] = [];
      const newReviewItems: ReviewItem[] = [];
      let skippedCount = 0;

      for (const rawEvent of discovered) {
        const year = rawEvent.year || new Date().getFullYear();
        const event: AIEvent = {
          id: generateEventId(rawEvent.campaignName || 'EVT', year),
          campaignName: rawEvent.campaignName || 'Unknown Event',
          category: rawEvent.category || 'General AI',
          url: rawEvent.url || '',
          priority: rawEvent.priority || '',
          year: year,
          month: rawEvent.month || (new Date().getMonth() + 1),
          startDate: rawEvent.startDate || '',
          endDate: rawEvent.endDate || '',
          location: rawEvent.location || 'Global',
          focusArea: rawEvent.focusArea || '',
          information: rawEvent.information || '',
          addedAt: new Date().toISOString()
        };

        if (isDuplicateEvent(event, existingEvents)) {
          skippedCount++;
          continue; 
        }

        // Double check against new items waiting in this very batch (prevent duplicates within the same scan result)
        if (isDuplicateEvent(event, newItems)) {
           skippedCount++;
           continue;
        }
        
        // Double check against review queue pending items (if using local queue)
        const pendingQueueEvents = storageService.getReviewQueue().map(r => r.event);
        if (isDuplicateEvent(event, pendingQueueEvents)) {
          skippedCount++;
          continue;
        }

        if (directSync) {
          setProgress(`Syncing "${event.campaignName}" to Cloud...`);
          if (supabaseService.isConfigured(config)) {
            await supabaseService.insertEvent(config, event);
          } else if (googleSheetsService.isConnected()) {
            await googleSheetsService.appendEvent(config, event);
          } else {
            storageService.addEvent(event);
          }
          newItems.push(event);
        } else {
          newReviewItems.push({
            type: 'NEW',
            event,
            status: 'PENDING'
          });
          // Temporarily add to newItems to prevent dupes in same batch if loop continues
          newItems.push(event); 
        }
      }

      const addedCount = directSync ? newItems.length : newReviewItems.length;

      setSummary({ 
        found: discovered.length, 
        new: addedCount, 
        skipped: skippedCount 
      });
      
      if (!directSync && newReviewItems.length > 0) {
        const currentQueue = storageService.getReviewQueue();
        storageService.saveReviewQueue([...currentQueue, ...newReviewItems]);
        setProgress(`Success: ${newReviewItems.length} leads in queue.`);
        setTimeout(() => onDiscoverComplete(), 2000);
      } else if (directSync && newItems.length > 0) {
        setProgress(`Direct Sync Successful: ${newItems.length} events added.`);
        setTimeout(() => onDiscoverComplete(), 2000);
      } else {
        setProgress(`Discovery finished. ${skippedCount} duplicates filtered.`);
        setTimeout(() => setIsSearching(false), 3000);
      }

    } catch (error: any) {
      console.error("Discovery Error:", error);
      const errorMessage = error.message || 'An error occurred during discovery.';
      setProgress(errorMessage);
      
      if (errorMessage.includes("API Key")) {
        setProgress("Error: Gemini API Key not found. Please set GEMINI_API_KEY in Vercel settings.");
      }
      
      setIsSearching(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-in fade-in duration-700">
      <div className={`max-w-3xl w-full border rounded-[2.5rem] p-12 text-center space-y-8 relative overflow-hidden transition-all duration-500 shadow-xl ${
        theme === 'dark' ? 'bg-[#0a0a0a] border-white/10' : 'bg-white border-black/5 shadow-slate-200/50'
      }`}>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent/50 to-transparent"></div>
        
        <div className={`w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto transition-all duration-1000 border ${
          isSearching 
            ? 'animate-pulse scale-110 shadow-[0_0_50px_rgba(139,62,255,0.1)] bg-accent/10 border-accent/40' 
            : theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-black/5'
        }`}>
          <svg className={`w-12 h-12 ${isSearching ? 'text-accent animate-spin-slow' : 'text-slate-400 dark:text-white/20'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <div className="space-y-3">
          <h2 className={`text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Discovery Engine</h2>
          <p className="text-slate-400 dark:text-white/40 max-w-md mx-auto text-sm leading-relaxed">
            Select focus areas for your intelligence scan. Choose between human review or direct automated sync.
          </p>
        </div>

        {!isSearching && (
          <div className="space-y-6">
            {/* Direct Sync Toggle */}
            <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all ${
              directSync 
                ? 'bg-accent/5 border-accent/20' 
                : theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-black/5'
            }`}>
              <div className="text-left">
                <h4 className={`text-[10px] font-bold uppercase tracking-widest ${directSync ? 'text-accent' : 'text-slate-400 dark:text-white/40'}`}>
                  Direct Cloud Sync
                </h4>
                <p className="text-[9px] text-slate-500 dark:text-white/20 mt-0.5">
                  Bypass the review queue and write discovered leads directly to Supabase.
                </p>
              </div>
              <button 
                onClick={() => setDirectSync(!directSync)}
                className={`relative w-12 h-6 rounded-full transition-all duration-300 ${directSync ? 'bg-accent' : 'bg-slate-300 dark:bg-white/10'}`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${directSync ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </button>
            </div>

            <div className={`rounded-3xl p-8 border text-left transition-colors ${
              theme === 'dark' ? 'bg-black/40 border-white/5' : 'bg-slate-50 border-black/5 shadow-inner'
            }`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-[10px] font-bold text-slate-400 dark:text-white/40 uppercase tracking-widest flex items-center gap-2">
                  <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 8.293A1 1 0 013 7.586V4z" /></svg>
                  Scan Constraints
                </h3>
                <div className="flex gap-4">
                  <button onClick={refreshKeywords} className="text-[10px] font-bold text-slate-500 hover:text-accent dark:text-white/30 dark:hover:text-white uppercase tracking-tighter transition-all" title="Reload default keywords">Refresh</button>
                  <button onClick={selectAll} className="text-[10px] font-bold text-accent hover:underline uppercase tracking-tighter transition-all">Select All</button>
                  <button onClick={clearAll} className="text-[10px] font-bold text-slate-400 dark:text-white/20 hover:text-red-400 uppercase tracking-tighter transition-all">Clear</button>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {allKeywords.map((k, i) => {
                  const isSelected = selectedKeywords.includes(k);
                  return (
                    <div key={i} className="relative group">
                      <button 
                        onClick={() => toggleKeyword(k)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all duration-300 flex items-center gap-2 pr-8 ${
                          isSelected 
                            ? 'bg-accent/10 border-accent/60 text-accent shadow-[0_0_15px_rgba(139,62,255,0.1)] scale-[1.02]' 
                            : theme === 'dark' 
                              ? 'bg-white/5 border-white/10 text-white/30 grayscale hover:grayscale-0 hover:border-white/30' 
                              : 'bg-white border-black/5 text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full transition-all ${isSelected ? 'bg-accent animate-pulse' : 'bg-current'}`}></div>
                        {k}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteKeyword(k);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                        title="Remove constraint"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  );
                })}
                
                <div className="flex items-center gap-2">
                  <input 
                    type="text"
                    placeholder="New constraint..."
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                    className={`px-4 py-2.5 rounded-xl text-xs font-semibold border bg-transparent focus:outline-none focus:border-accent/60 transition-all w-32 ${
                      theme === 'dark' ? 'border-white/10 text-white' : 'border-black/5 text-slate-900'
                    }`}
                  />
                  <button 
                    onClick={addKeyword}
                    className="p-2.5 rounded-xl bg-accent/10 border border-accent/20 text-accent hover:bg-accent/20 transition-all"
                    title="Add Constraint"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 min-h-[100px] flex flex-col items-center justify-center">
          {!isSearching ? (
            <button 
              onClick={startDiscovery}
              disabled={selectedKeywords.length === 0}
              className={`px-10 py-5 font-bold rounded-2xl transition-all flex items-center gap-3 mx-auto shadow-2xl active:scale-95 ${
                selectedKeywords.length > 0 
                  ? 'bg-accent text-black hover:scale-105 shadow-accent/20' 
                  : 'bg-slate-100 dark:bg-white/5 text-slate-300 dark:text-white/10 cursor-not-allowed border border-black/5 dark:border-white/5'
              }`}
            >
              {selectedKeywords.length > 0 ? `Launch ${selectedKeywords.length} Focus Scan` : 'Select topics to scan'}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </button>
          ) : (
            <div className="space-y-6 w-full max-w-sm">
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-accent font-bold text-sm tracking-wide animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent"></span>
                  {progress}
                </div>
                {summary && (
                  <div className="flex gap-4 text-[10px] uppercase font-bold tracking-[0.2em] text-slate-500 dark:text-white/20">
                    <span>{summary.found} Found</span>
                    <span>•</span>
                    <span>{summary.skipped} Deduplicated</span>
                  </div>
                )}
              </div>
              <div className={`w-full h-1.5 rounded-full overflow-hidden transition-colors ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'}`}>
                <div className="h-full bg-accent animate-[shimmer_2s_infinite] w-1/3 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        .animate-spin-slow {
          animation: spin 4s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DiscoveryTool;
